// How sessions ended (D113 phase 1, spec 6.4), as Health and App exits read the API's /exits:
// the reasons the console lists, their tone, and one release against the one before, per 1,000
// sessions. As the mock says, a session counts once, under its reason, and exits the system
// makes while the app is in the background (memory_pressure) are not counted; nor are normal
// exits, which are not a way the app stopped on anyone.
import type { ExitReason, Exits } from '../api/types'
import type { MessageId } from '../i18n'

/** Reasons the API counts that the console does not list as exits. */
const NOT_LISTED: readonly ExitReason[] = ['memory_pressure', 'normal']

export type ExitRelease = Exits['releases'][number]

/** Whether the console lists `reason` as an app exit. */
export const listed = (reason: ExitReason) => !NOT_LISTED.includes(reason)

/** A reason's name, as the mock writes it; memory is the jetsam limit on iOS. */
export function exitLabel(reason: ExitReason, android: boolean): MessageId {
  return reason === 'memory' && android ? 'exits.reason.memoryAndroid' : `exits.reason.${reason}`
}

/** The bar's tone: a crash red; a force quit, memory or an ANR amber; the rest ink. */
export function exitTone(reason: ExitReason): 'bad' | 'warn' | undefined {
  if (reason === 'crash') return 'bad'
  return reason === 'user_quit' || reason === 'memory' || reason === 'anr' ? 'warn' : undefined
}

/** A release's exits per 1,000 sessions for `reason`; 0 when none ended that way. */
export function per1kOf(r: ExitRelease | undefined, reason: ExitReason): number {
  return r?.reasons.find((x) => x.reason === reason)?.per_1k_sessions ?? 0
}

/** A release's listed exits per 1,000 sessions, all reasons together. */
export function allExits(r: ExitRelease | undefined): number {
  return (r?.reasons ?? [])
    .filter((x) => listed(x.reason))
    .reduce((n, x) => n + x.per_1k_sessions, 0)
}

export interface ExitRow {
  reason: ExitReason
  /** Per 1,000 sessions in the release. */
  v: number
  /** Per 1,000 sessions in the release before it; null without one. */
  p: number | null
}

/**
 * The listed reasons of a release and the one before it, the most in the release first: every
 * reason either one has, so a reason that went away still shows its drop.
 */
export function exitRows(latest: ExitRelease | undefined, previous?: ExitRelease): ExitRow[] {
  const reasons = new Set<ExitReason>()
  for (const r of [latest, previous])
    for (const x of r?.reasons ?? []) if (listed(x.reason)) reasons.add(x.reason)
  return [...reasons]
    .map((reason) => ({
      reason,
      v: per1kOf(latest, reason),
      p: previous ? per1kOf(previous, reason) : null,
    }))
    .sort((a, b) => b.v - a.v || (b.p ?? 0) - (a.p ?? 0) || a.reason.localeCompare(b.reason))
}

/**
 * Where each reason comes from (spec 6.4 and 7.3), per platform: iOS (MetricKit, or the SDK's
 * inference at the next launch) and Android (ApplicationExitInfo). A platform without an entry
 * does not report the reason.
 */
export const EXIT_SOURCES: Partial<Record<ExitReason, { ios?: MessageId; android?: MessageId }>> = {
  crash: { ios: 'exits.src.ios.crash', android: 'exits.src.android.crash' },
  user_quit: { ios: 'exits.src.ios.user_quit', android: 'exits.src.android.user_quit' },
  memory: { ios: 'exits.src.ios.memory', android: 'exits.src.android.memory' },
  background_task_timeout: { ios: 'exits.src.ios.background_task_timeout' },
  os_update: { ios: 'exits.src.ios.os_update', android: 'exits.src.android.os_update' },
  app_update: { ios: 'exits.src.ios.app_update', android: 'exits.src.android.app_update' },
  watchdog: { ios: 'exits.src.ios.watchdog' },
  anr: { android: 'exits.src.android.anr' },
  cpu: { ios: 'exits.src.ios.cpu' },
  locked_file: { ios: 'exits.src.ios.locked_file' },
  signal: { android: 'exits.src.android.signal' },
  unknown: { ios: 'exits.src.ios.unknown', android: 'exits.src.android.unknown' },
}
